/* dde-form-guard.js — bot defence for the DEG public forms (DEGSHOPIFY-200/-201).
 *
 * Three things, all local. No Google, no Cloudflare, no third-party script:
 *
 *   1. stamps `form_ts` so the backend can reject submissions completed faster
 *      than a human could type;
 *   2. carries the honeypot fields rendered by snippets/dde-form-guard.liquid;
 *   3. solves an ALTCHA proof-of-work challenge issued by our own backend and
 *      puts the solution in the hidden `altcha` field.
 *
 * This implements the ALTCHA protocol directly rather than loading their widget
 * bundle — the whole point of choosing it over reCAPTCHA/Turnstile was that no
 * third-party code runs on the storefront, and vendoring someone's bundle would
 * have given that away for no benefit. It is about sixty lines.
 *
 * The work starts as soon as the form is on the page, so by the time a real
 * person has finished typing the solution is already there and the submit is
 * instant. A bot that posts immediately either waits for the CPU or fails.
 */
(function () {
  'use strict';

  var API = (window.DDE_API_BASE || 'https://api.deg-sued.de');
  var CHALLENGE_URL = API + '/api/altcha/challenge';

  function hex(buf) {
    var v = new Uint8Array(buf), s = '';
    for (var i = 0; i < v.length; i++) s += v[i].toString(16).padStart(2, '0');
    return s;
  }

  // Kurz den Ereignisstrom freigeben, ohne setTimeout.
  //
  // ⚠️ setTimeout wird in NICHT SICHTBAREN Tabs auf etwa einen Aufruf pro
  // Sekunde gedrosselt. Da solve() alle 1024 Runden pausiert, zog sich eine
  // Aufgabe dort auf eine Minute und mehr — beobachtet am 26.08., als das
  // Formular in einem Hintergrundtab minutenlang gar keine Loesung hatte.
  // Das trifft echte Kunden: wer die Seite in einem zweiten Tab oeffnet und
  // erst spaeter hinwechselt, steht sonst vor einem Formular, das noch rechnet.
  //
  // MessageChannel unterliegt dieser Drosselung nicht und gibt den Strom
  // trotzdem frei, die Seite ruckelt also weiterhin nicht.
  var breathe = (function () {
    if (typeof MessageChannel !== 'function') {
      return function () { return new Promise(function (r) { setTimeout(r, 0); }); };
    }
    return function () {
      return new Promise(function (resolve) {
        var ch = new MessageChannel();
        ch.port1.onmessage = function () { ch.port1.close(); resolve(); };
        ch.port2.postMessage(0);
      });
    };
  })();

  // Brute-force n such that SHA-256(salt + n) === challenge.
  // Yields to the event loop periodically so the page never janks.
  async function solve(salt, challenge, maxnumber) {
    var enc = new TextEncoder();
    for (var n = 0; n <= maxnumber; n++) {
      var digest = await crypto.subtle.digest('SHA-256', enc.encode(salt + n));
      if (hex(digest) === challenge) return n;
      if ((n & 0x3ff) === 0) await breathe();
    }
    return null;
  }

  async function fetchChallenge() {
    // Retry a few times with backoff. Once enforcement is on, a form with no
    // solution is refused, so a single transient failure here would cost a real
    // customer their enquiry — worth three attempts before giving up.
    var lastErr;
    for (var attempt = 0; attempt < 3; attempt++) {
      try {
        var res = await fetch(CHALLENGE_URL, { credentials: 'omit', cache: 'no-store' });
        if (res.ok) return await res.json();
        lastErr = new Error('challenge ' + res.status);
      } catch (e) {
        lastErr = e;
      }
      await new Promise(function (r) { setTimeout(r, 400 * (attempt + 1)); });
    }
    throw lastErr || new Error('challenge unavailable');
  }

  async function obtainSolution() {
    var c = await fetchChallenge();
    var number = await solve(c.salt, c.challenge, c.maxnumber);
    if (number === null) throw new Error('unsolved');
    return btoa(JSON.stringify({
      algorithm: c.algorithm,
      challenge: c.challenge,
      number: number,
      salt: c.salt,
      signature: c.signature,
    }));
  }

  // Eine Loesung je altcha-Feld, und die naechste laeuft schon im Hintergrund.
  //
  // ⚠️ Eine ALTCHA-Loesung ist EINMALIG. api/src/altcha.js merkt sich jede
  // benutzte und verwirft eine Wiederholung STILL mit HTTP 200. Bis 2026-08-26
  // lag die einmal geloeste Aufgabe dauerhaft im Feld und ging bei jedem
  // weiteren Absenden erneut raus: das erste Absenden wurde gespeichert, jedes
  // weitere ohne Neuladen lief in den Replay-Schutz — und weil der mit 200
  // antwortet, meldete das Formular trotzdem "Vielen Dank", obwohl nichts
  // ankam. Belegt im Netzwerkmitschnitt: zwei POST mit identischem altcha,
  // 201 und danach 200, in der Datenbank genau eine Zeile.
  //
  // Keyed auf das Feld statt auf ein <form>, weil nicht jede geschuetzte
  // Flaeche eines hat — sections/anfrageformular.liquid besteht nur aus divs.
  var slots = new WeakMap();

  function nextSolution() {
    return obtainSolution().catch(function (e) {
      // Einen echten Kunden nie blockieren, nur weil unser Endpunkt zickt.
      // Die anderen drei Schichten greifen weiterhin.
      console.warn('[dde-form-guard] altcha unavailable:', e && e.message);
      return null;
    });
  }

  function slotFor(field) {
    var s = slots.get(field);
    if (!s) { s = { current: nextSolution() }; slots.set(field, s); }
    return s;
  }

  // Where to look for the sibling fields: the enclosing form if there is one,
  // otherwise the field's own container.
  function scopeOf(field) {
    return field.closest('form') || field.parentElement || document;
  }

  // ⚠️ Das Feld beschreibt AUSSCHLIESSLICH arm(). Ein Hintergrundlauf darf dem
  // Abschnitt den Wert nicht austauschen, waehrend er ihn gerade ausliest.
  function arm(field) {
    if (!field) return Promise.resolve(null);
    var scope = scopeOf(field);
    var tsField = scope.querySelector('[name="form_ts"]');
    if (tsField && !tsField.value) tsField.value = String(Date.now());
    return slotFor(field).current.then(function (sol) {
      if (sol) field.value = sol;
      return sol;
    });
  }

  // Exposed so the AJAX submit handlers in the sections can await the solution
  // before they POST — see snippets/dde-form-guard.liquid for the contract.
  // Accepts a <form>, any container, or the altcha field itself.
  window.ddeFormGuardReady = function (el) {
    if (!el) return Promise.resolve(null);
    var field = (el.name === 'altcha') ? el : el.querySelector('[name="altcha"]');
    if (!field) return Promise.resolve(null);
    return arm(field).then(function (sol) {
      // Verbraucht. Die naechste Aufgabe jetzt anstossen, damit ein zweites
      // Absenden ohne Neuladen weder am Replay-Schutz scheitert noch wartet.
      slotFor(field).current = nextSolution();
      return sol;
    });
  };

  function init() {
    // Discovered via the hidden field the snippet renders rather than an
    // attribute on the <form>: Shopify's {% form %} tag is fussy about
    // hyphenated attributes, and this needs no change to the tag at all.
    var fields = document.querySelectorAll('input[name="altcha"]');
    for (var i = 0; i < fields.length; i++) arm(fields[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
